import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollWipeContext } from "./scrollwipeContext";

const clamp01 = (n) => Math.max(0, Math.min(1, n));

export default function ScrollWipeGroup({ children, seam = 0.5 }) {
  const rootRef = useRef(null);
  const lengthsRef = useRef(new Map());
  const orderRef = useRef([]);
  const [version, setVersion] = useState(0);
  const [t, setT] = useState(0);
  const tRef = useRef(0); // ← ADD THIS

  const register = useCallback((id, len) => {
    const lengths = lengthsRef.current;
    const prev = lengths.get(id);
    if (prev === len) return;
    if (!lengths.has(id)) {
      orderRef.current.push(id);
    }
    lengths.set(id, len);
    setVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((id) => {
    const lengths = lengthsRef.current;
    if (!lengths.has(id)) return;
    lengths.delete(id);
    orderRef.current = orderRef.current.filter((x) => x !== id);
    setVersion((v) => v + 1);
  }, []);

  const layout = useMemo(() => {
    let total = 0;
    const offsets = new Map();
    for (const id of orderRef.current) {
      offsets.set(id, total);
      total += lengthsRef.current.get(id) || 0;
    }
    return { offsets, total };
  }, [version]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let raf = 0;

    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const seamY = seam * vh;
      const raw = (seamY - r.top) / Math.max(1, r.height);
      const clamped = clamp01(raw);
      tRef.current = clamped; // ← UPDATE REF
      setT(clamped);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [seam]);

  // t is NOT in this useMemo — group reference stays stable across scrolls
  const value = useMemo(
    () => ({
      register,
      unregister,
      seam,
      tRef, // ← PASS REF INSTEAD OF t
      offsets: layout.offsets,
      total: layout.total,
    }),
    [register, unregister, seam, layout.offsets, layout.total]
  );

  return (
    <ScrollWipeContext.Provider value={value}>
      <div ref={rootRef} className="ScrollWipeGroup">
        {children}
      </div>
    </ScrollWipeContext.Provider>
  );
}
